import { useState } from "react";
import useAuth from "../hooks/useAuth";

// ============================================================================
// LANDING — v15
// Hero (intro) → tab pills (What is Onward? · Get Started · Why we're different ·
// Our Purpose) → sign-off → footer. Fork widget lives inside the "Get Started"
// tab. Persona-ending CTAs stash { persona, fork2, fork3 } into sessionStorage
// so SignupFlow can pre-fill intent / timeline / notes from picks the user
// already made on this page.
// ============================================================================

const SAGE = {
  50: "#f6f7f4", 100: "#eaede5", 200: "#d5dbcc", 300: "#b5c0a5",
  400: "#8fa17a", 500: "#6b8455", 600: "#546a42", 700: "#3f4f32",
  800: "#2d3824", 900: "#1c2317",
};
const CREAM = {
  50: "#fefdfb", 100: "#fdf9f1", 200: "#faf3e3", 300: "#f5ead0", 400: "#ecdcb3",
};
const WARM = {
  accent: "#c4956a", accentSoft: "#e8d5c0",
  rose: "#c48a8a", roseSoft: "#f0e0e0",
  blue: "#6a7fc4", blueSoft: "#dde3f3",
  green: "#7a9975", greenSoft: "#dde8d9",
};

const F_DISPLAY = "'Fraunces', Georgia, serif";
const F_BODY = "'Outfit', sans-serif";
const F_MONO = "'JetBrains Mono', monospace";

// Layout helpers — Tailwind isn't in the build pipeline (CSS-in-JS only),
// so max-w-* / mx-auto / px-* utilities are inert. Use these inline-style
// constants on layout containers instead.
const MAX_2XL = { maxWidth: 672,  margin: "0 auto", paddingLeft: 32, paddingRight: 32 };
const MAX_3XL = { maxWidth: 768,  margin: "0 auto", paddingLeft: 32, paddingRight: 32 };
const MAX_5XL = { maxWidth: 1024, margin: "0 auto", paddingLeft: 32, paddingRight: 32 };
const MAX_6XL = { maxWidth: 1152, margin: "0 auto", paddingLeft: 32, paddingRight: 32 };
const MAX_7XL = { maxWidth: 1280, margin: "0 auto", paddingLeft: 32, paddingRight: 32 };

// ============================================================================
// PERSONA DATA (fork widget)
// ============================================================================

const PERSONAS = {
  seeker: {
    id: "seeker", name: "Seeker", label: "I just need a job.", sublabel: "Something real, soon.",
    color: WARM.rose, colorSoft: WARM.roseSoft, icon: "▲", stampLabel: "seeker",
    fork2: { question: "When do you need it?", prompt: "We'll calibrate the work to your timeline.", options: [
      { id: "week",    label: "This week",    detail: "I need something fast." },
      { id: "month",   label: "This month",   detail: "I have a little runway." },
      { id: "quarter", label: "This quarter", detail: "I want to move deliberately." },
    ]},
    fork3: { question: "What kind of role are you after?", prompt: "We'll set up your search accordingly.", options: [
      { id: "same",     label: "Similar to my last one", detail: "Keep moving in this direction." },
      { id: "adjacent", label: "Adjacent but different", detail: "Related field, new environment." },
      { id: "open",     label: "Open to what fits",      detail: "I care more about fit than title." },
    ]},
    ending: {
      headline: "Let's get you applying — today.",
      body: "We'll build your profile quickly, match you to live roles that actually fit, and generate a tailored resume for each one.",
      cta: "Start with Job Search",
      features: [
        "Upload your current resume — we'll extract everything",
        "See matching roles ranked by fit, not keywords",
        "Generate a tailored resume per role in under a minute",
        "Track every application from one place",
      ],
    },
  },
  explorer: {
    id: "explorer", name: "Explorer", label: "I want to explore.", sublabel: "Show me what's possible.",
    color: WARM.blue, colorSoft: WARM.blueSoft, icon: "◈", stampLabel: "explorer",
    fork2: { question: "What's pulling at you?", prompt: "No wrong answer. We'll work from here.", options: [
      { id: "field",   label: "A change in field",  detail: "Different industry, different work." },
      { id: "stage",   label: "A change in stage",  detail: "IC to manager, startup to big co." },
      { id: "curious", label: "Just curious",       detail: "Not sure yet. Want options." },
    ]},
    fork3: { question: "What's your time horizon?", prompt: "No pressure — this just helps us pace things.", options: [
      { id: "months",  label: "Next few months",    detail: "Looking around actively." },
      { id: "year",    label: "Sometime this year", detail: "Thinking ahead." },
      { id: "someday", label: "Someday",            detail: "Planting seeds for later." },
    ]},
    ending: {
      headline: "Let's see what's possible for you.",
      body: "We'll start with your profile and surface directions you might not have considered. No pressure. Just a wider view.",
      cta: "Start with a Profile",
      features: [
        "Build a rich profile of your whole career",
        "See roles, fields, and paths you'd be a strong fit for",
        "Explore at your own pace — no goal required",
        "When you're ready, we'll help you narrow in",
      ],
    },
  },
  pivoter: {
    id: "pivoter", name: "Pivoter", label: "I'm changing directions.", sublabel: "Something meaningfully different.",
    color: WARM.accent, colorSoft: WARM.accentSoft, icon: "◆", stampLabel: "pivoter",
    fork2: { question: "Do you know where to?", prompt: "Any of these is a fine place to start.", options: [
      { id: "specific", label: "Yes, specifically", detail: "I know the role I want." },
      { id: "roughly",  label: "Roughly",           detail: "Direction's clear, details aren't." },
      { id: "notyet",   label: "Not yet",           detail: "I want change, but not to what." },
    ]},
    fork3: { question: "What's your biggest worry?", prompt: "We'll tackle this first.", options: [
      { id: "feasible", label: "Is this pivot feasible?",  detail: "I need to know it's realistic." },
      { id: "path",     label: "What's the actual path?",  detail: "I need steps, not just vision." },
      { id: "identity", label: "Am I really that person?", detail: "I need to feel credible in it." },
    ]},
    ending: {
      headline: "Let's map the route.",
      body: "Pivots are about closing the distance between who you are and who you're becoming. We'll help you see that distance clearly and choose the moves that close it fastest.",
      cta: "Set Your Pivot Goal",
      features: [
        "Name where you're going — we'll help you be specific",
        "See the gap: skills, evidence, and story shifts needed",
        "Get a tailored plan: classes, projects, stretch roles",
        "Track your readiness score as it climbs",
      ],
    },
  },
  climber: {
    id: "climber", name: "Climber", label: "I'm climbing.", sublabel: "I know where — faster would be better.",
    color: WARM.green, colorSoft: WARM.greenSoft, icon: "●", stampLabel: "climber",
    fork2: { question: "What's your timeline?", prompt: "We'll scale the recommendations to your runway.", options: [
      { id: "6mo",  label: "6 months",  detail: "I'm ready to move now." },
      { id: "18mo", label: "18 months", detail: "I want to be deliberate." },
      { id: "long", label: "Long game", detail: "3-5 years. Compound moves." },
    ]},
    fork3: { question: "What's the bottleneck?", prompt: "Climbing is about finding the rate-limiter.", options: [
      { id: "skills",     label: "Skills or scope",       detail: "I need to grow into the next level." },
      { id: "visibility", label: "Visibility",            detail: "I do the work; the right people don't see it." },
      { id: "network",    label: "Network & opportunity", detail: "The right rooms, the right doors." },
    ]},
    ending: {
      headline: "Let's compound your next moves.",
      body: "Climbing isn't about working harder — it's about picking the moves with the highest leverage. We'll identify the classes, projects, and relationships that compound.",
      cta: "Set Your Growth Goal",
      features: [
        "Define your next-level target (role, scope, or impact)",
        "See which moves compound fastest for your timeline",
        "Stay continuously ready for internal or external opportunities",
        "Measure leverage, not just activity",
      ],
    },
  },
};

const PERSONA_ORDER = ["seeker", "explorer", "pivoter", "climber"];

// ============================================================================
// FORK WIDGET — geometry & helpers
// ============================================================================

const VB = { width: 1200, height: 340 };
const ORIGIN = { x: 60, y: 170 };

const PERSONA_POINTS = {
  seeker:   { x: 420, y: 60  },
  explorer: { x: 420, y: 140 },
  pivoter:  { x: 420, y: 215 },
  climber:  { x: 420, y: 295 },
};

function getFork2Points(personaKey) {
  const base = PERSONA_POINTS[personaKey];
  return [-44, 0, 44].map(dy => ({ x: 780, y: Math.max(35, Math.min(305, base.y + dy)) }));
}
function getFork3Points(parentPoint) {
  return [-36, 0, 36].map(dy => ({ x: 1140, y: Math.max(35, Math.min(305, parentPoint.y + dy)) }));
}
function curve(p1, p2) {
  const midX = (p1.x + p2.x) / 2;
  return `M ${p1.x} ${p1.y} C ${midX} ${p1.y}, ${midX} ${p2.y}, ${p2.x} ${p2.y}`;
}

function PickCard({ p, leftPct, topPct, width, offsetX, leftSide, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        position: "absolute",
        left: leftSide ? "auto" : `calc(${leftPct}% + ${offsetX}px)`,
        right: leftSide ? `calc(${100 - leftPct}% + ${Math.abs(offsetX)}px)` : "auto",
        top: `${topPct}%`,
        transform: "translateY(-50%)",
        width,
        background: CREAM[50], border: `1.5px solid ${SAGE[200]}`,
        borderRadius: 11, padding: "10px 12px", textAlign: "left",
        cursor: "pointer", transition: "all 0.2s", fontFamily: F_BODY,
        boxShadow: `0 2px 8px ${SAGE[800]}0a`,
      }}
      onMouseEnter={e => {
        const tx = leftSide ? "-2px" : "2px";
        e.currentTarget.style.transform = `translateY(-50%) translateX(${tx})`;
        e.currentTarget.style.borderColor = p.color;
        e.currentTarget.style.background = p.colorSoft;
        e.currentTarget.style.boxShadow = `0 8px 20px ${p.color}33`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = "translateY(-50%)";
        e.currentTarget.style.borderColor = SAGE[200];
        e.currentTarget.style.background = CREAM[50];
        e.currentTarget.style.boxShadow = `0 2px 8px ${SAGE[800]}0a`;
      }}
    >
      {children}
    </button>
  );
}

function ForkWidget({ onPersonaSignIn }) {
  const [stage, setStage] = useState(0);
  const [personaKey, setPersonaKey] = useState(null);
  const [fork2Choice, setFork2Choice] = useState(null);
  const [fork3Choice, setFork3Choice] = useState(null);

  const persona = personaKey ? PERSONAS[personaKey] : null;
  const fork2Points = personaKey ? getFork2Points(personaKey) : [];
  const chosenFork2Point = fork2Choice ? fork2Points[persona.fork2.options.findIndex(o => o.id === fork2Choice)] : null;
  const fork3Points = chosenFork2Point ? getFork3Points(chosenFork2Point) : [];
  const chosenFork3Point = fork3Choice ? fork3Points[persona.fork3.options.findIndex(o => o.id === fork3Choice)] : null;

  function pickPersona(key) { setPersonaKey(key); setTimeout(() => setStage(1), 120); }
  function pickFork2(id) { setFork2Choice(id); setTimeout(() => setStage(2), 120); }
  function pickFork3(id) { setFork3Choice(id); setTimeout(() => setStage(3), 120); }
  function reset() {
    setStage(0);
    setTimeout(() => { setPersonaKey(null); setFork2Choice(null); setFork3Choice(null); }, 400);
  }
  function back() {
    if (stage === 0) return;
    if (stage === 1) { setStage(0); setTimeout(() => setPersonaKey(null), 300); }
    else if (stage === 2) { setStage(1); setTimeout(() => setFork2Choice(null), 300); }
    else if (stage === 3) { setStage(2); setTimeout(() => setFork3Choice(null), 300); }
  }

  function handlePersonaCta() {
    if (!personaKey) return;
    onPersonaSignIn?.({ persona: personaKey, fork2: fork2Choice, fork3: fork3Choice });
  }

  return (
    <div style={{
      background: `radial-gradient(ellipse at 20% 10%, ${CREAM[200]}80 0%, transparent 60%), radial-gradient(ellipse at 85% 85%, ${SAGE[100]}99 0%, transparent 60%), ${CREAM[50]}`,
      border: `1px solid ${SAGE[200]}`, borderRadius: 24, overflow: "hidden",
      position: "relative", boxShadow: `0 2px 0 ${SAGE[100]}, 0 40px 80px -40px ${SAGE[800]}22`,
    }}>
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: `radial-gradient(circle at 1px 1px, ${SAGE[200]}40 1px, transparent 0), radial-gradient(circle at 8px 12px, ${WARM.accentSoft}30 1px, transparent 0)`,
        backgroundSize: "14px 14px, 22px 22px", opacity: 0.4,
      }} />

      <div style={{
        position: "relative", zIndex: 2, padding: "16px 28px",
        borderBottom: `1px solid ${SAGE[200]}88`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: `${CREAM[50]}99`, backdropFilter: "blur(4px)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <svg width="18" height="18" viewBox="0 0 22 22">
            <circle cx="11" cy="11" r="9" fill="none" stroke={SAGE[300]} strokeWidth="1" />
            <path d="M 11 3 L 13 11 L 11 19 L 9 11 Z" fill={SAGE[700]} />
            <path d="M 3 11 L 11 9 L 19 11 L 11 13 Z" fill={WARM.accent} opacity="0.85" />
            <circle cx="11" cy="11" r="1.6" fill={CREAM[50]} />
          </svg>
          <span style={{ fontSize: 11, color: SAGE[500], letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 600 }}>
            Your trail
          </span>
        </div>
        <div style={{ fontSize: 12, color: SAGE[500], fontStyle: "italic", fontFamily: F_DISPLAY }}>
          {stage === 0 && "Pick a starting direction."}
          {stage === 1 && persona && `Walking the ${persona.name} path...`}
          {stage === 2 && "One more fork ahead..."}
          {stage === 3 && "Here's where the road leads."}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {stage > 0 && (
            <button onClick={back} style={{
              background: "transparent", border: `1px solid ${SAGE[300]}`,
              borderRadius: 999, padding: "4px 12px", fontSize: 12, color: SAGE[600],
              cursor: "pointer", fontFamily: F_BODY,
            }}>
              ← back
            </button>
          )}
          {stage === 0 && <div style={{ width: 60 }} />}
        </div>
      </div>

      <div style={{ position: "relative", padding: "28px 28px 36px" }}>
        <div style={{ textAlign: "center", marginBottom: 20, minHeight: 72 }}>
          {stage === 0 && (
            <>
              <div style={{ fontFamily: F_MONO, fontSize: 10.5, color: SAGE[500], letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 600, marginBottom: 8 }}>
                Where are you on the road?
              </div>
              <h2 style={{ fontFamily: F_DISPLAY, fontSize: "clamp(24px, 3vw, 32px)", fontWeight: 300, letterSpacing: "-0.025em", color: SAGE[900], lineHeight: 1.1 }}>
                Which traveler are you today?
              </h2>
            </>
          )}
          {stage === 1 && persona && (
            <>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 10,
                padding: "4px 12px", borderRadius: 999,
                background: persona.colorSoft, border: `1px solid ${persona.color}66`,
                marginBottom: 10,
              }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: persona.color }} />
                <span style={{ fontSize: 11, color: persona.color, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                  {persona.name}
                </span>
              </div>
              <h2 style={{ fontFamily: F_DISPLAY, fontSize: "clamp(24px, 3vw, 32px)", fontWeight: 300, letterSpacing: "-0.025em", color: SAGE[900], lineHeight: 1.1 }}>
                {persona.fork2.question}
              </h2>
            </>
          )}
          {stage === 2 && persona && (
            <>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "center", marginBottom: 10 }}>
                <span style={{ padding: "3px 10px", borderRadius: 999, background: persona.colorSoft, fontSize: 11, color: persona.color, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                  {persona.name}
                </span>
                <span style={{ color: SAGE[300], fontSize: 14 }}>›</span>
                <span style={{ padding: "3px 10px", borderRadius: 999, background: persona.colorSoft, fontSize: 11, color: persona.color, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                  {persona.fork2.options.find(o => o.id === fork2Choice)?.label}
                </span>
              </div>
              <h2 style={{ fontFamily: F_DISPLAY, fontSize: "clamp(24px, 3vw, 32px)", fontWeight: 300, letterSpacing: "-0.025em", color: SAGE[900], lineHeight: 1.1 }}>
                {persona.fork3.question}
              </h2>
            </>
          )}
          {stage === 3 && persona && (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
              <span style={{ fontSize: 12, color: SAGE[500] }}>Your route:</span>
              <span style={{ fontFamily: F_DISPLAY, fontSize: 13, fontStyle: "italic", color: persona.color, fontWeight: 500, padding: "3px 10px", borderRadius: 999, background: persona.colorSoft }}>
                {persona.name}
              </span>
              <span style={{ color: SAGE[300] }}>›</span>
              <span style={{ fontFamily: F_DISPLAY, fontSize: 13, fontStyle: "italic", color: persona.color, fontWeight: 500, padding: "3px 10px", borderRadius: 999, background: persona.colorSoft }}>
                {persona.fork2.options.find(o => o.id === fork2Choice)?.label.toLowerCase()}
              </span>
              <span style={{ color: SAGE[300] }}>›</span>
              <span style={{ fontFamily: F_DISPLAY, fontSize: 13, fontStyle: "italic", color: persona.color, fontWeight: 500, padding: "3px 10px", borderRadius: 999, background: persona.colorSoft }}>
                {persona.fork3.options.find(o => o.id === fork3Choice)?.label.toLowerCase()}
              </span>
            </div>
          )}
        </div>

        <div style={{ position: "relative", width: "100%", aspectRatio: `${VB.width} / ${VB.height}` }}>
          <svg viewBox={`0 0 ${VB.width} ${VB.height}`} style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
            <defs>
              <filter id="forkRough">
                <feTurbulence type="fractalNoise" baseFrequency="0.02" numOctaves="2" />
                <feDisplacementMap in="SourceGraphic" scale="1.4" />
              </filter>
              <pattern id="trailDots" width="22" height="22" patternUnits="userSpaceOnUse">
                <circle cx="11" cy="11" r="0.5" fill={SAGE[300]} opacity="0.5" />
              </pattern>
            </defs>
            <rect width={VB.width} height={VB.height} fill="url(#trailDots)" opacity="0.4" />
            <g opacity="0.2" stroke={SAGE[400]} fill="none" strokeWidth="0.8">
              <path d={`M 0 80 Q 400 60, 800 80 T ${VB.width} 70`} />
              <path d={`M 0 260 Q 400 240, 800 260 T ${VB.width} 250`} />
            </g>
            {[420, 780, 1140].map(x => (
              <line key={x} x1={x} y1="10" x2={x} y2="25" stroke={SAGE[300]} strokeWidth="0.6" opacity="0.5" />
            ))}

            {stage === 0 && PERSONA_ORDER.map(key => {
              const p = PERSONAS[key];
              return (
                <path key={key} d={curve(ORIGIN, PERSONA_POINTS[key])}
                  fill="none" stroke={p.color} strokeWidth="2" strokeLinecap="round"
                  strokeDasharray="4 4" opacity="0.85" style={{ filter: "url(#forkRough)" }} />
              );
            })}

            {stage >= 1 && personaKey && (
              <>
                <path d={curve(ORIGIN, PERSONA_POINTS[personaKey])}
                  fill="none" stroke={persona.color} strokeWidth="3" strokeLinecap="round"
                  style={{ filter: "url(#forkRough)" }} />
                {PERSONA_ORDER.filter(k => k !== personaKey).map(key => {
                  const p = PERSONAS[key];
                  return (
                    <path key={key} d={curve(ORIGIN, PERSONA_POINTS[key])}
                      fill="none" stroke={p.color} strokeWidth="1.5" strokeLinecap="round"
                      strokeDasharray="4 4" opacity="0.15" />
                  );
                })}
              </>
            )}

            {stage === 1 && personaKey && fork2Points.map((pt, i) => (
              <path key={i} d={curve(PERSONA_POINTS[personaKey], pt)}
                fill="none" stroke={persona.color} strokeWidth="2" strokeLinecap="round"
                strokeDasharray="4 4" opacity="0.85" style={{ filter: "url(#forkRough)" }} />
            ))}

            {stage >= 2 && personaKey && chosenFork2Point && (
              <>
                <path d={curve(PERSONA_POINTS[personaKey], chosenFork2Point)}
                  fill="none" stroke={persona.color} strokeWidth="3" strokeLinecap="round"
                  style={{ filter: "url(#forkRough)" }} />
                {fork2Points.filter((_, i) => persona.fork2.options[i].id !== fork2Choice).map((pt, i) => (
                  <path key={i} d={curve(PERSONA_POINTS[personaKey], pt)}
                    fill="none" stroke={persona.color} strokeWidth="1.5" strokeLinecap="round"
                    strokeDasharray="4 4" opacity="0.15" />
                ))}
              </>
            )}

            {stage === 2 && chosenFork2Point && fork3Points.map((pt, i) => (
              <path key={i} d={curve(chosenFork2Point, pt)}
                fill="none" stroke={persona.color} strokeWidth="2" strokeLinecap="round"
                strokeDasharray="4 4" opacity="0.85" style={{ filter: "url(#forkRough)" }} />
            ))}

            {stage === 3 && chosenFork2Point && chosenFork3Point && (
              <>
                <path d={curve(chosenFork2Point, chosenFork3Point)}
                  fill="none" stroke={persona.color} strokeWidth="3" strokeLinecap="round"
                  style={{ filter: "url(#forkRough)" }} />
                {fork3Points.filter((_, i) => persona.fork3.options[i].id !== fork3Choice).map((pt, i) => (
                  <path key={i} d={curve(chosenFork2Point, pt)}
                    fill="none" stroke={persona.color} strokeWidth="1.5" strokeLinecap="round"
                    strokeDasharray="4 4" opacity="0.15" />
                ))}
              </>
            )}

            <g transform={`translate(${ORIGIN.x}, ${ORIGIN.y})`}>
              <circle r="20" fill="none" stroke={SAGE[300]} strokeWidth="1" strokeDasharray="2 3" />
              <circle r="10" fill={SAGE[700]} />
              <circle r="3" fill={CREAM[50]} />
              <text y="-28" textAnchor="middle" fontFamily={F_DISPLAY} fontSize="11" fontStyle="italic" fontWeight="500" fill={SAGE[800]}>
                you are here
              </text>
            </g>

            {stage >= 1 && personaKey && (
              <g transform={`translate(${PERSONA_POINTS[personaKey].x}, ${PERSONA_POINTS[personaKey].y})`}>
                <circle r="14" fill="none" stroke={persona.color} strokeWidth="1" strokeDasharray="2 2" opacity="0.5" />
                <circle r="9" fill={persona.color} />
                <text y="3.5" textAnchor="middle" fontSize="10" fontFamily={F_DISPLAY} fill={CREAM[50]} fontWeight="500">
                  {persona.icon}
                </text>
                {stage >= 2 && (
                  <text y="-22" textAnchor="middle" fontFamily={F_DISPLAY} fontSize="10.5" fontStyle="italic" fill={persona.color} fontWeight="500">
                    {persona.name.toLowerCase()}
                  </text>
                )}
              </g>
            )}

            {stage >= 2 && chosenFork2Point && (
              <g transform={`translate(${chosenFork2Point.x}, ${chosenFork2Point.y})`}>
                <circle r="14" fill="none" stroke={persona.color} strokeWidth="1" strokeDasharray="2 2" opacity="0.5" />
                <circle r="9" fill={persona.color} />
                <circle r="3" fill={CREAM[50]} />
                {stage >= 3 && (
                  <text y="-22" textAnchor="middle" fontFamily={F_DISPLAY} fontSize="10.5" fontStyle="italic" fill={persona.color} fontWeight="500">
                    {persona.fork2.options.find(o => o.id === fork2Choice)?.label.toLowerCase()}
                  </text>
                )}
              </g>
            )}

            {stage >= 3 && chosenFork3Point && (
              <>
                <g transform={`translate(${chosenFork3Point.x}, ${chosenFork3Point.y})`}>
                  <circle r="14" fill="none" stroke={persona.color} strokeWidth="1" strokeDasharray="2 2" opacity="0.5" />
                  <circle r="9" fill={persona.color} />
                  <circle r="3" fill={CREAM[50]} />
                  <text y="-22" textAnchor="middle" fontFamily={F_DISPLAY} fontSize="10.5" fontStyle="italic" fill={persona.color} fontWeight="500">
                    {persona.fork3.options.find(o => o.id === fork3Choice)?.label.toLowerCase()}
                  </text>
                </g>
                <path d={`M ${chosenFork3Point.x + 14} ${chosenFork3Point.y} L ${Math.min(chosenFork3Point.x + 44, VB.width - 24)} ${chosenFork3Point.y - 16}`}
                  fill="none" stroke={persona.color} strokeWidth="1.5" strokeLinecap="round"
                  strokeDasharray="3 4" opacity="0.6" />
              </>
            )}
          </svg>

          {stage === 0 && PERSONA_ORDER.map(key => {
            const p = PERSONAS[key];
            const pt = PERSONA_POINTS[key];
            return (
              <PickCard key={p.id} p={p}
                leftPct={(pt.x / VB.width) * 100} topPct={(pt.y / VB.height) * 100}
                width="clamp(140px, 18%, 180px)" offsetX={22}
                onClick={() => pickPersona(p.id)}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
                  <span style={{ width: 18, height: 18, borderRadius: "50%", background: p.color, color: CREAM[50], display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9.5, fontFamily: F_DISPLAY }}>
                    {p.icon}
                  </span>
                  <span style={{ fontFamily: F_DISPLAY, fontSize: 13, fontStyle: "italic", color: p.color, fontWeight: 500 }}>
                    {p.name}
                  </span>
                </div>
                <div style={{ fontFamily: F_DISPLAY, fontSize: 14, fontWeight: 500, color: SAGE[900], lineHeight: 1.25, letterSpacing: "-0.01em" }}>
                  {p.label}
                </div>
                <div style={{ fontSize: 11, color: SAGE[600], lineHeight: 1.35, marginTop: 2 }}>
                  {p.sublabel}
                </div>
              </PickCard>
            );
          })}

          {stage === 1 && persona && fork2Points.map((pt, i) => {
            const opt = persona.fork2.options[i];
            return (
              <PickCard key={opt.id} p={persona}
                leftPct={(pt.x / VB.width) * 100} topPct={(pt.y / VB.height) * 100}
                width="clamp(140px, 18%, 180px)" offsetX={22}
                onClick={() => pickFork2(opt.id)}>
                <div style={{ fontFamily: F_DISPLAY, fontSize: 15, fontWeight: 500, color: SAGE[900], lineHeight: 1.2, letterSpacing: "-0.01em" }}>
                  {opt.label}
                </div>
                <div style={{ fontSize: 11.5, color: SAGE[600], lineHeight: 1.35, marginTop: 3 }}>
                  {opt.detail}
                </div>
              </PickCard>
            );
          })}

          {stage === 2 && persona && fork3Points.map((pt, i) => {
            const opt = persona.fork3.options[i];
            return (
              <PickCard key={opt.id} p={persona}
                leftPct={(pt.x / VB.width) * 100} topPct={(pt.y / VB.height) * 100}
                width="clamp(140px, 16%, 170px)" offsetX={-22} leftSide
                onClick={() => pickFork3(opt.id)}>
                <div style={{ fontFamily: F_DISPLAY, fontSize: 14.5, fontWeight: 500, color: SAGE[900], lineHeight: 1.2, letterSpacing: "-0.01em" }}>
                  {opt.label}
                </div>
                <div style={{ fontSize: 11, color: SAGE[600], lineHeight: 1.35, marginTop: 3 }}>
                  {opt.detail}
                </div>
              </PickCard>
            );
          })}
        </div>

        {stage > 0 && stage < 3 && (
          <p style={{ textAlign: "center", fontSize: 13, color: SAGE[500], fontStyle: "italic", fontFamily: F_DISPLAY, marginTop: 12 }}>
            {stage === 1 && persona.fork2.prompt}
            {stage === 2 && persona.fork3.prompt}
          </p>
        )}

        {stage === 3 && persona && (
          <div style={{ marginTop: 26, paddingTop: 26, borderTop: `1px solid ${SAGE[200]}` }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "flex-start" }}>
              <div style={{ flex: "1 1 380px", minWidth: 0 }}>
                <h3 style={{ fontFamily: F_DISPLAY, fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 300, letterSpacing: "-0.025em", color: SAGE[900], lineHeight: 1.05, marginBottom: 14 }}>
                  {persona.ending.headline}
                </h3>
                <p style={{ fontSize: 15, color: SAGE[600], lineHeight: 1.6, fontWeight: 400, marginBottom: 22 }}>
                  {persona.ending.body}
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", marginBottom: 24 }}>
                  <button
                    onClick={handlePersonaCta}
                    style={{ background: persona.color, color: CREAM[50], padding: "12px 22px", borderRadius: 999, fontSize: 14.5, fontWeight: 500, border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, fontFamily: F_BODY }}
                  >
                    {persona.ending.cta}
                    <span style={{ fontFamily: F_DISPLAY, fontStyle: "italic" }}>→</span>
                  </button>
                  <button onClick={reset} style={{ background: "transparent", color: SAGE[600], padding: "12px 16px", borderRadius: 999, fontSize: 13.5, fontWeight: 500, cursor: "pointer", border: `1px solid ${SAGE[300]}`, fontFamily: F_BODY }}>
                    Try a different path
                  </button>
                </div>
              </div>
              <div style={{ flex: "1 1 280px", minWidth: 0 }}>
                <div style={{ background: CREAM[50], border: `1px solid ${SAGE[200]}`, borderRadius: 14, padding: "20px 22px" }}>
                  <div style={{ fontSize: 10.5, color: SAGE[500], letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 600, marginBottom: 12 }}>
                    What happens next
                  </div>
                  <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {persona.ending.features.map((f, i) => (
                      <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "9px 0", borderTop: i === 0 ? "none" : `1px solid ${SAGE[100]}` }}>
                        <span style={{ flexShrink: 0, marginTop: 3, color: persona.color, fontSize: 11, fontFamily: F_DISPLAY }}>◆</span>
                        <span style={{ fontSize: 13, color: SAGE[700], lineHeight: 1.5 }}>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{ position: "relative", zIndex: 2, padding: "14px 28px", borderTop: `1px solid ${SAGE[200]}88`, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: `${CREAM[50]}99` }}>
        {[0, 1, 2, 3].map(i => (
          <span key={i} style={{ width: stage === i ? 22 : 7, height: 7, borderRadius: 4, background: stage === i ? (persona?.color || SAGE[600]) : SAGE[300], transition: "all 0.3s" }} />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

const TABS = [
  { id: "what",      label: "What is Onward?" },
  { id: "purpose",   label: "Our Purpose" },
  { id: "different", label: "Why we're different" },
  { id: "start",     label: "Get Started" },
];

export default function LandingForkRoadPage() {
  const { handleLogin } = useAuth();
  const [activeTab, setActiveTab] = useState("what");

  const onSignIn = (e) => { if (e) e.preventDefault(); handleLogin(); };

  // ForkWidget passes { persona, fork2, fork3 } so SignupFlow can pre-fill
  // intent / timeline / notes from picks already made on the landing page.
  // Persisted to sessionStorage; consumed once on first signup.
  const onPersonaSignIn = (journey) => {
    const personaKey = journey?.persona;
    if (personaKey) {
      try { sessionStorage.setItem('onward_persona', personaKey); } catch (_) {}
    }
    if (journey && typeof journey === 'object') {
      try { sessionStorage.setItem('onward_journey', JSON.stringify(journey)); } catch (_) {}
    }
    handleLogin();
  };

  return (
    <div style={{ background: CREAM[100], minHeight: "100vh", fontFamily: F_BODY, color: SAGE[900], overflow: "hidden" }}>
      <link href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,400;0,9..144,500;0,9..144,600;1,9..144,300;1,9..144,400;1,9..144,500&family=Outfit:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet" />

      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .fade-up { animation: fadeUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; }
        .d1 { animation-delay: 0.08s; } .d2 { animation-delay: 0.22s; }
        .d3 { animation-delay: 0.36s; } .d4 { animation-delay: 0.5s; }
        @keyframes panelIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .panel-in { animation: panelIn 0.45s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .btn-primary { transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1); }
        .btn-primary:hover { transform: translateY(-1px); box-shadow: 0 10px 20px rgba(45, 56, 36, 0.15); }
        .link-u {
          background-image: linear-gradient(to right, ${WARM.accent}, ${WARM.accent});
          background-repeat: no-repeat; background-size: 100% 2px;
          background-position: 0 100%; padding-bottom: 2px;
        }
      `}</style>

      <div style={{ position: "relative", zIndex: 1 }}>

        {/* ============ NAV ============ */}
        <nav style={{ ...MAX_7XL, paddingTop: 24, paddingBottom: 24, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <svg width="22" height="22" viewBox="0 0 22 22">
              <circle cx="11" cy="11" r="9" fill="none" stroke={SAGE[300]} strokeWidth="1" />
              <path d="M 11 3 L 13 11 L 11 19 L 9 11 Z" fill={SAGE[700]} />
              <path d="M 3 11 L 11 9 L 19 11 L 11 13 Z" fill={WARM.accent} opacity="0.85" />
              <circle cx="11" cy="11" r="1.6" fill={CREAM[50]} />
            </svg>
            <span style={{ fontFamily: F_DISPLAY, fontSize: 22, fontWeight: 500, letterSpacing: "-0.02em", color: SAGE[900] }}>
              Onward
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "6px 12px", borderRadius: 999,
              background: CREAM[50], border: `1px solid ${SAGE[200]}`,
            }}>
              <svg width="8" height="8" viewBox="0 0 10 10">
                <circle cx="5" cy="5" r="4" fill={WARM.accent} />
              </svg>
              <span style={{ fontSize: 11.5, color: SAGE[600], fontWeight: 500, letterSpacing: "-0.005em" }}>
                Private beta · by invitation
              </span>
            </div>
            <button
              onClick={onSignIn}
              className="btn-primary"
              style={{ fontSize: 13, color: CREAM[50], background: SAGE[800], border: "none", cursor: "pointer", fontWeight: 500, fontFamily: F_BODY, padding: "10px 20px", borderRadius: 999 }}
            >
              Sign in
            </button>
          </div>
        </nav>

        {/* ============ HERO ============ */}
        <section style={{ ...MAX_5XL, paddingTop: 8, paddingBottom: 24, textAlign: "center" }}>
          <h1 className="fade-up d1" style={{
            fontFamily: F_DISPLAY, fontSize: "clamp(40px, 5.5vw, 72px)", fontWeight: 300,
            lineHeight: 0.98, letterSpacing: "-0.035em", color: SAGE[900], marginBottom: 18,
          }}>
            Your career,<br/>
            <span style={{ fontStyle: "italic", color: WARM.accent, fontWeight: 400 }}>ready before you need it.</span>
          </h1>

          <p className="fade-up d2" style={{
            fontSize: 17, lineHeight: 1.55, color: SAGE[600], fontWeight: 400, maxWidth: 580, margin: "0 auto",
          }}>
            More than a job board. More than a resume builder. Onward holds your story across every chapter —
            and has it shaped for whatever's next.
          </p>
        </section>

        {/* ============ TAB PILLS ============ */}
        <section style={{ ...MAX_7XL, paddingBottom: 0 }}>
          <div style={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 8,
            padding: "4px 0 16px",
          }}>
            {TABS.map((t) => {
              const isActive = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  style={{
                    background: isActive ? SAGE[800] : CREAM[50],
                    color: isActive ? CREAM[50] : SAGE[700],
                    border: `1px solid ${isActive ? SAGE[800] : SAGE[200]}`,
                    borderRadius: 999,
                    padding: "9px 18px",
                    minWidth: 190,
                    textAlign: "center",
                    cursor: "pointer",
                    fontFamily: F_BODY,
                    fontSize: 13.5,
                    fontWeight: 500,
                    letterSpacing: "-0.005em",
                    transition: "all 0.2s",
                    boxShadow: isActive ? `0 4px 12px -4px ${SAGE[800]}55` : "none",
                  }}
                  onMouseEnter={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = WARM.accentSoft;
                      e.currentTarget.style.borderColor = WARM.accent;
                      e.currentTarget.style.color = SAGE[900];
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = CREAM[50];
                      e.currentTarget.style.borderColor = SAGE[200];
                      e.currentTarget.style.color = SAGE[700];
                    }
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </section>

        {/* ============ TAB PANELS ============ */}
        <section style={{ borderTop: `1px solid ${SAGE[200]}`, background: CREAM[50] }}>

          {/* Panel: What is Onward? */}
          {activeTab === "what" && (
            <div className="panel-in" key="what" style={{ ...MAX_3XL, paddingTop: 24, paddingBottom: 56 }}>
              <h2 style={{
                fontFamily: F_DISPLAY, fontSize: "clamp(28px, 3.4vw, 40px)", fontWeight: 300,
                lineHeight: 1.2, letterSpacing: "-0.025em", color: SAGE[900], marginBottom: 26,
              }}>
                A Career Builder.<br/>
                <span style={{ fontStyle: "italic", color: SAGE[600] }}>Not a job board. Not a resume builder.</span>
              </h2>

              <p style={{ fontSize: 18, lineHeight: 1.7, color: SAGE[700], fontWeight: 400, marginBottom: 22, maxWidth: 640 }}>
                Onward is the place where your full career lives. Every project, every accomplishment, every story —
                kept current, kept structured, kept ready.
              </p>
              <p style={{ fontSize: 18, lineHeight: 1.7, color: SAGE[700], fontWeight: 400, marginBottom: 22, maxWidth: 640 }}>
                When the right role appears, your application is minutes away — not days.
                When you want to pivot, your route is mapped.
                When you're not actively looking, your readiness keeps quietly compounding.
              </p>
              <p style={{ fontSize: 18, lineHeight: 1.7, color: SAGE[700], fontWeight: 400, marginBottom: 28, maxWidth: 640 }}>
                <span style={{ fontStyle: "italic", color: SAGE[800] }}>You tell us your story.</span> We take care of the rest.
              </p>

              <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                <button
                  onClick={() => setActiveTab("start")}
                  className="btn-primary"
                  style={{ background: SAGE[800], color: CREAM[50], fontSize: 14, fontWeight: 500, border: "none", cursor: "pointer", fontFamily: F_BODY, display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 24px", borderRadius: 999 }}
                >
                  Pick your path <span style={{ fontFamily: F_DISPLAY, fontStyle: "italic" }}>→</span>
                </button>
                <button
                  onClick={() => setActiveTab("different")}
                  style={{ background: "transparent", border: "none", padding: "12px 4px", cursor: "pointer", fontFamily: F_BODY, fontSize: 13.5, color: SAGE[700], fontWeight: 500 }}
                >
                  See why we're different <span style={{ color: WARM.accent }}>›</span>
                </button>
              </div>
            </div>
          )}

          {/* Panel: Get Started — straight into the widget. The widget's own
              header (kicker + "Which traveler are you today?") is the only
              prompt the user needs; the prior panel preamble was redundant. */}
          {activeTab === "start" && (
            <div className="panel-in" key="start" style={{ ...MAX_7XL, paddingTop: 24, paddingBottom: 56 }}>
              <ForkWidget onPersonaSignIn={onPersonaSignIn} />
            </div>
          )}

          {/* Panel: Why we're different */}
          {activeTab === "different" && (
            <div className="panel-in" key="different" style={{ ...MAX_6XL, paddingTop: 24, paddingBottom: 56 }}>
              <div style={{ marginBottom: 36 }}>
                <h2 style={{
                  fontFamily: F_DISPLAY, fontSize: "clamp(28px, 3.8vw, 46px)", fontWeight: 300,
                  lineHeight: 1.1, letterSpacing: "-0.025em", color: SAGE[900], marginBottom: 18, maxWidth: 820,
                }}>
                  Finding your next job<br/>
                  <span style={{ fontStyle: "italic", color: WARM.accent }}>shouldn't be about guessing the magic words.</span>
                </h2>
                <p style={{ fontSize: 17, color: SAGE[600], lineHeight: 1.6, fontWeight: 400, maxWidth: 600 }}>
                  Three things we do differently — and why they matter for the kind of career you're building.
                </p>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>

                {/* CARD 1 — Magic Wand */}
                <div style={{
                  background: CREAM[50],
                  border: `1px solid ${SAGE[200]}`,
                  borderRadius: 18,
                  padding: "28px 26px 26px",
                  position: "relative",
                }}>
                  <div style={{
                    height: 160, marginBottom: 22,
                    background: `linear-gradient(180deg, ${CREAM[100]} 0%, ${CREAM[50]} 100%)`,
                    borderRadius: 12,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: `1px solid ${SAGE[100]}`,
                  }}>
                    <svg viewBox="0 0 200 140" style={{ width: "85%", height: "100%" }}>
                      <g opacity="0.6">
                        <path d="M 38 38 L 40 32 L 42 38 L 48 40 L 42 42 L 40 48 L 38 42 L 32 40 Z" fill={WARM.accent}/>
                        <path d="M 28 56 L 29 52 L 30 56 L 34 57 L 30 58 L 29 62 L 28 58 L 24 57 Z" fill={WARM.accent} opacity="0.7"/>
                        <path d="M 54 24 L 55 21 L 56 24 L 59 25 L 56 26 L 55 29 L 54 26 L 51 25 Z" fill={WARM.accent} opacity="0.5"/>
                      </g>
                      <g transform="translate(60, 40) rotate(35 50 35)">
                        <rect x="20" y="33" width="80" height="6" rx="2.5"
                              fill={CREAM[200]} stroke={SAGE[600]} strokeWidth="1.4"/>
                        <rect x="92" y="32" width="14" height="8" rx="1"
                              fill={SAGE[800]} stroke={SAGE[800]} strokeWidth="0.8"/>
                        <rect x="20" y="32" width="14" height="8" rx="1"
                              fill={SAGE[800]} stroke={SAGE[800]} strokeWidth="0.8"/>
                      </g>
                      <g>
                        <circle cx="100" cy="70" r="50" fill="none"
                                stroke={WARM.rose} strokeWidth="3.5" strokeLinecap="round"
                                opacity="0.85"/>
                        <line x1="63" y1="105" x2="138" y2="35"
                              stroke={WARM.rose} strokeWidth="3.5" strokeLinecap="round"
                              opacity="0.85"/>
                      </g>
                    </svg>
                  </div>

                  <div style={{
                    fontFamily: F_MONO, fontSize: 10.5, color: WARM.accent,
                    letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 600, marginBottom: 10,
                  }}>
                    No magic words
                  </div>
                  <h3 style={{
                    fontFamily: F_DISPLAY, fontSize: 22, fontWeight: 500,
                    color: SAGE[900], lineHeight: 1.25, letterSpacing: "-0.015em", marginBottom: 12,
                  }}>
                    Your next job shouldn't be about<br/>
                    <span style={{ fontStyle: "italic", color: SAGE[700] }}>guessing the magic words.</span>
                  </h3>
                  <p style={{ fontSize: 14.5, color: SAGE[600], lineHeight: 1.6, fontWeight: 400 }}>
                    Most tools optimize for keyword matchers. We work to understand what a role is actually
                    asking for — and what your career actually shows. Substance, not surface.
                  </p>
                </div>

                {/* CARD 2 — Rabbit in Hat */}
                <div style={{
                  background: CREAM[50],
                  border: `1px solid ${SAGE[200]}`,
                  borderRadius: 18,
                  padding: "28px 26px 26px",
                  position: "relative",
                }}>
                  <div style={{
                    height: 160, marginBottom: 22,
                    background: `linear-gradient(180deg, ${CREAM[100]} 0%, ${CREAM[50]} 100%)`,
                    borderRadius: 12,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: `1px solid ${SAGE[100]}`,
                  }}>
                    <svg viewBox="0 0 200 140" style={{ width: "85%", height: "100%" }}>
                      <g transform="translate(100, 70)">
                        <ellipse cx="-10" cy="-20" rx="6" ry="22" fill={CREAM[200]} stroke={SAGE[700]} strokeWidth="1.4"
                                 transform="rotate(-12 -10 -20)"/>
                        <ellipse cx="-10" cy="-22" rx="3" ry="14" fill={WARM.roseSoft}
                                 transform="rotate(-12 -10 -22)" opacity="0.7"/>
                        <ellipse cx="10" cy="-20" rx="6" ry="22" fill={CREAM[200]} stroke={SAGE[700]} strokeWidth="1.4"
                                 transform="rotate(12 10 -20)"/>
                        <ellipse cx="10" cy="-22" rx="3" ry="14" fill={WARM.roseSoft}
                                 transform="rotate(12 10 -22)" opacity="0.7"/>
                        <ellipse cx="0" cy="14" rx="44" ry="6" fill={SAGE[800]} stroke={SAGE[900]} strokeWidth="1"/>
                        <rect x="-26" y="-12" width="52" height="26" rx="2"
                              fill={SAGE[800]} stroke={SAGE[900]} strokeWidth="1"/>
                        <ellipse cx="0" cy="-12" rx="26" ry="3.5" fill={SAGE[900]}/>
                        <rect x="-26" y="6" width="52" height="4" fill={WARM.accent} opacity="0.85"/>
                      </g>
                      <g>
                        <circle cx="100" cy="70" r="55" fill="none"
                                stroke={WARM.rose} strokeWidth="3.5" strokeLinecap="round"
                                opacity="0.85"/>
                        <line x1="61" y1="109" x2="139" y2="31"
                              stroke={WARM.rose} strokeWidth="3.5" strokeLinecap="round"
                              opacity="0.85"/>
                      </g>
                    </svg>
                  </div>

                  <div style={{
                    fontFamily: F_MONO, fontSize: 10.5, color: WARM.accent,
                    letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 600, marginBottom: 10,
                  }}>
                    No invention
                  </div>
                  <h3 style={{
                    fontFamily: F_DISPLAY, fontSize: 22, fontWeight: 500,
                    color: SAGE[900], lineHeight: 1.25, letterSpacing: "-0.015em", marginBottom: 12,
                  }}>
                    We don't pull rabbits<br/>
                    <span style={{ fontStyle: "italic", color: SAGE[700] }}>out of hats.</span>
                  </h3>
                  <p style={{ fontSize: 14.5, color: SAGE[600], lineHeight: 1.6, fontWeight: 400 }}>
                    We get the full story from you — projects you actually shipped, work you actually did.
                    Always your words, not ours. Always true.
                  </p>
                </div>

                {/* CARD 3 — Quality vs Quantity scatter */}
                <div style={{
                  background: CREAM[50],
                  border: `1px solid ${SAGE[200]}`,
                  borderRadius: 18,
                  padding: "28px 26px 26px",
                  position: "relative",
                }}>
                  <div style={{
                    height: 160, marginBottom: 22,
                    background: `linear-gradient(180deg, ${CREAM[100]} 0%, ${CREAM[50]} 100%)`,
                    borderRadius: 12,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: `1px solid ${SAGE[100]}`,
                    padding: "14px 16px",
                  }}>
                    <svg viewBox="0 0 200 140" style={{ width: "100%", height: "100%" }}>
                      <defs>
                        <pattern id="qqGrid" width="20" height="20" patternUnits="userSpaceOnUse">
                          <path d="M 20 0 L 0 0 0 20" fill="none" stroke={SAGE[200]} strokeWidth="0.5" opacity="0.6"/>
                        </pattern>
                      </defs>
                      <rect x="30" y="14" width="148" height="100" fill="url(#qqGrid)"/>

                      <line x1="30" y1="114" x2="178" y2="114" stroke={SAGE[400]} strokeWidth="1.2"/>
                      <line x1="30" y1="14" x2="30" y2="114" stroke={SAGE[400]} strokeWidth="1.2"/>

                      <text x="14" y="62" textAnchor="middle" fontFamily={F_MONO} fontSize="8"
                            fill={SAGE[500]} transform="rotate(-90 14 62)" letterSpacing="1">
                        QUALITY
                      </text>
                      <text x="104" y="130" textAnchor="middle" fontFamily={F_MONO} fontSize="8"
                            fill={SAGE[500]} letterSpacing="1">
                        QUANTITY
                      </text>

                      <path d="M 178 114 L 174 111 L 174 117 Z" fill={SAGE[400]}/>
                      <path d="M 30 14 L 27 18 L 33 18 Z" fill={SAGE[400]}/>

                      <circle cx="138" cy="92" r="4" fill={SAGE[400]} opacity="0.5"/>
                      <circle cx="155" cy="100" r="4" fill={SAGE[400]} opacity="0.5"/>
                      <circle cx="120" cy="98" r="4" fill={SAGE[400]} opacity="0.5"/>

                      <circle cx="50" cy="34" r="4" fill={SAGE[400]} opacity="0.5"/>
                      <circle cx="58" cy="48" r="4" fill={SAGE[400]} opacity="0.5"/>

                      <circle cx="156" cy="32" r="14" fill={WARM.accent} opacity="0.18"/>
                      <circle cx="156" cy="32" r="9" fill={WARM.accent} opacity="0.35"/>
                      <circle cx="156" cy="32" r="5.5" fill={WARM.accent}/>
                      <circle cx="156" cy="32" r="2" fill={CREAM[50]}/>

                      <text x="156" y="20" textAnchor="middle" fontFamily={F_DISPLAY}
                            fontSize="10" fontStyle="italic" fill={WARM.accent} fontWeight="500">
                        Onward
                      </text>
                    </svg>
                  </div>

                  <div style={{
                    fontFamily: F_MONO, fontSize: 10.5, color: WARM.accent,
                    letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 600, marginBottom: 10,
                  }}>
                    No trade-offs
                  </div>
                  <h3 style={{
                    fontFamily: F_DISPLAY, fontSize: 22, fontWeight: 500,
                    color: SAGE[900], lineHeight: 1.25, letterSpacing: "-0.015em", marginBottom: 12,
                  }}>
                    We don't trade<br/>
                    <span style={{ fontStyle: "italic", color: SAGE[700] }}>quality for quantity.</span>
                  </h3>
                  <p style={{ fontSize: 14.5, color: SAGE[600], lineHeight: 1.6, fontWeight: 400 }}>
                    Most tools force the choice — ten generic applications, or one polished one.
                    We refuse it. You shouldn't have to either.
                  </p>
                </div>

              </div>
            </div>
          )}

          {/* Panel: Our Purpose */}
          {activeTab === "purpose" && (
            <div className="panel-in" key="purpose" style={{ ...MAX_6XL, paddingTop: 24, paddingBottom: 56 }}>
              <div style={{ marginBottom: 32, maxWidth: 672 }}>
                <h2 style={{
                  fontFamily: F_DISPLAY, fontSize: "clamp(28px, 4vw, 48px)", fontWeight: 300,
                  lineHeight: 1.05, letterSpacing: "-0.03em", color: SAGE[900], marginBottom: 14,
                }}>
                  The job search is harrying.<br/>
                  <span style={{ fontStyle: "italic", color: SAGE[600] }}>It doesn't have to be.</span>
                </h2>
                <p style={{ fontSize: 16, color: SAGE[600], lineHeight: 1.6, fontWeight: 400, maxWidth: 600 }}>
                  We built Onward because the way most people manage their careers feels frantic when it doesn't have to.
                  You bring you. We bring everything else.
                </p>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 24 }}>
                <div style={{
                  background: CREAM[100],
                  border: `1px solid ${SAGE[200]}`,
                  borderRadius: 18,
                  padding: "28px 28px 24px",
                }}>
                  <div style={{
                    fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase",
                    color: SAGE[500], fontWeight: 600, marginBottom: 10,
                  }}>
                    What it feels like now
                  </div>
                  <h3 style={{
                    fontFamily: F_DISPLAY, fontSize: 22, fontWeight: 500,
                    color: SAGE[800], lineHeight: 1.2, letterSpacing: "-0.015em", marginBottom: 18,
                  }}>
                    Harrying.
                  </h3>
                  <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {[
                      "Squeezing your career onto two pages.",
                      "Rewriting the same resume forty different ways.",
                      "Fielding curveballs from forty different recruiters.",
                      "Wondering if any of this is actually working.",
                      "Scrambling when the right role appears — not before.",
                    ].map((s, i) => (
                      <li key={i} style={{
                        display: "flex", alignItems: "flex-start", gap: 10,
                        padding: "7px 0",
                        fontSize: 14.5, color: SAGE[600], lineHeight: 1.5,
                      }}>
                        <span style={{ color: WARM.rose, fontSize: 13, lineHeight: 1.4, marginTop: 2, flexShrink: 0 }}>×</span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div style={{
                  background: CREAM[50],
                  border: `1.5px solid ${WARM.accent}`,
                  borderRadius: 18,
                  padding: "28px 28px 24px",
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: `0 12px 32px -16px ${WARM.accent}66`,
                }}>
                  <div style={{
                    position: "absolute", top: 0, left: 0, right: 0, height: 4,
                    background: `linear-gradient(to right, ${SAGE[600]}, ${WARM.accent})`,
                  }}/>
                  <div style={{
                    fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase",
                    color: WARM.accent, fontWeight: 600, marginBottom: 10,
                  }}>
                    What it could feel like
                  </div>
                  <h3 style={{
                    fontFamily: F_DISPLAY, fontSize: 22, fontWeight: 500,
                    color: SAGE[900], lineHeight: 1.2, letterSpacing: "-0.015em", marginBottom: 18,
                  }}>
                    At ease.
                  </h3>
                  <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {[
                      "Telling your story once, deeply.",
                      "Living your life while we shape the format.",
                      "Knowing your readiness is quietly compounding.",
                      "Sending applications you actually mean.",
                      "Being ready before opportunity knocks.",
                    ].map((s, i) => (
                      <li key={i} style={{
                        display: "flex", alignItems: "flex-start", gap: 10,
                        padding: "7px 0",
                        fontSize: 14.5, color: SAGE[700], lineHeight: 1.5,
                      }}>
                        <span style={{ color: WARM.accent, fontSize: 13, lineHeight: 1.4, marginTop: 2, flexShrink: 0 }}>◆</span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

        </section>

        {/* ============ SIGN-OFF ============ */}
        <section style={{ background: SAGE[800], color: CREAM[50] }}>
          <div style={{ ...MAX_7XL, paddingTop: 80, paddingBottom: 80 }}>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 32,
              flexWrap: "wrap",
            }}>
              <div>
                <div style={{
                  fontFamily: F_DISPLAY,
                  fontSize: "clamp(32px, 4.5vw, 56px)",
                  fontWeight: 300,
                  lineHeight: 1,
                  letterSpacing: "-0.03em",
                  color: CREAM[50],
                }}>
                  Always ready.<br/>
                  <span style={{ fontStyle: "italic", color: WARM.accent }}>Never rushed.</span>
                </div>
                <div style={{
                  fontFamily: F_DISPLAY, fontSize: 28, fontWeight: 500, color: CREAM[100],
                  letterSpacing: "-0.02em", marginTop: 14,
                }}>
                  Onward.
                </div>
              </div>

              <div style={{ textAlign: "right" }}>
                <button
                  onClick={onSignIn}
                  className="btn-primary"
                  style={{ background: WARM.accent, color: CREAM[50], fontSize: 15, fontWeight: 500, border: "none", cursor: "pointer", fontFamily: F_BODY, display: "inline-flex", alignItems: "center", gap: 8, padding: "16px 32px", borderRadius: 999 }}
                >
                  Request an invitation <span style={{ fontFamily: F_DISPLAY, fontStyle: "italic" }}>→</span>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ============ FOOTER ============ */}
        <footer style={{ ...MAX_7XL, paddingTop: 40, paddingBottom: 40, display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: `1px solid ${SAGE[200]}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <svg width="16" height="16" viewBox="0 0 22 22">
              <circle cx="11" cy="11" r="9" fill="none" stroke={SAGE[300]} strokeWidth="1" />
              <path d="M 11 4 L 12.5 11 L 11 18 L 9.5 11 Z" fill={SAGE[600]} />
              <circle cx="11" cy="11" r="1.2" fill={CREAM[50]} />
            </svg>
            <span style={{ fontFamily: F_DISPLAY, fontSize: 15, fontWeight: 500, color: SAGE[700] }}>Onward</span>
          </div>
          <span style={{ fontSize: 12, color: SAGE[400], letterSpacing: "0.05em" }}>
            © {new Date().getFullYear()} · Seattle · Made to last
          </span>
        </footer>
      </div>
    </div>
  );
}
